import * as Sentry from "@sentry/react";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";

import axios from "axios";
import { CLEAR_MAP_ID } from "constants/app.constants";
import {
  FACILITY_LABEL_LAYER,
  SHAPEFILE_DETAILS_LABEL_LAYER,
  SHAPEFILE_NAME_LABELS_LAYER,
  WELL_LABEL_LAYER
} from "constants/mapLayers.constants";
import _debounce from "lodash/debounce";
import { selectLabelDensity, selectMapZoom, useMapStore } from "stores/map";
import { getFromRecord } from "utils/fp";

const mapServiceEndpoint = process.env.REACT_APP_MAP_SERVICE;

export function setLabelVisibility(
  mapbox: mapboxgl.Map,
  layer: string,
  hasEntities: boolean,
  labelSize: number,
  density = 0
) {
  if (!mapbox.getLayer(layer)) {
    return;
  }
  let adjustedSize = labelSize;
  if (density > DENSITY_FONT_SCALE_THRESHOLD) {
    const scaleFactor =
      1 - (density - DENSITY_FONT_SCALE_THRESHOLD) * FONT_SCALE_REDUCTION;
    adjustedSize = Math.max(MIN_LABEL_FONT_SIZE, Math.round(labelSize * scaleFactor));
  }
  mapbox.setLayoutProperty(layer, "text-size", hasEntities ? adjustedSize : 0);
}

const DENSITY_FULL = 0.95;
const DENSITY_HIGH = 0.7;
const DENSITY_FONT_SCALE_THRESHOLD = 0.5;
const FONT_SCALE_REDUCTION = 0.6;
const MIN_LABEL_FONT_SIZE = 8;
const DEFAULT_TEXT_PADDING = 4;
const MAX_TEXT_PADDING = 10;
const DENSITY_DEBOUNCE_MS = 300;
const DEFAULT_SYMBOL_SPACING = 250;
const MIN_SYMBOL_SPACING = 50;
export const LABEL_DENSITY_MIN_ZOOM = 8;
const DEFAULT_DENSITY = 0;
const VARIABLE_ANCHORS = [
  "top",
  "bottom",
  "left",
  "right",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right"
];

/**
 * Collects all label layer IDs from the map: well, facility, and dynamic shapefile layers.
 */
export function getLabelLayerIds(mapbox: mapboxgl.Map): string[] {
  const layers: string[] = [WELL_LABEL_LAYER, FACILITY_LABEL_LAYER];

  let style: mapboxgl.Style | null = null;
  try {
    style = mapbox.getStyle();
  } catch {
    // getStyle() throws when a style change is in progress — skip shapefile layers
  }
  if (style?.layers) {
    for (const l of style.layers) {
      if (l.id.endsWith("-shapefile-label") || l.id.endsWith("-property")) {
        layers.push(l.id);
      }
    }
  }

  return layers;
}

/**
 * Maps a map layer ID to its density category key.
 * Dynamic shapefile layers map back to their parent layer constant.
 */
export function getDensityKeyForLayer(layerId: string): string {
  if (layerId === WELL_LABEL_LAYER) return WELL_LABEL_LAYER;
  if (layerId === FACILITY_LABEL_LAYER) return FACILITY_LABEL_LAYER;
  if (layerId.endsWith("-shapefile-label")) return SHAPEFILE_NAME_LABELS_LAYER;
  if (layerId.endsWith("-property")) return SHAPEFILE_DETAILS_LABEL_LAYER;
  return layerId;
}

/**
 * Returns the collision layout properties for a given density tier.
 */
export function getCollisionProperties(density: number) {
  if (density >= DENSITY_FULL) {
    return {
      "text-allow-overlap": true,
      "text-ignore-placement": true
    };
  }
  if (density >= DENSITY_HIGH) {
    return {
      "text-allow-overlap": false,
      "text-ignore-placement": false,
      "text-variable-anchor": [...VARIABLE_ANCHORS],
      "text-padding": Math.max(1, Math.round(MAX_TEXT_PADDING * (1 - density)))
    };
  }
  return {
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-variable-anchor": ["top", "bottom", "left", "right"],
    "text-padding": DEFAULT_TEXT_PADDING
  };
}

/**
 * Applies collision properties to a single layer.
 * Skips text-variable-anchor for layers using symbol-placement: "line" (incompatible).
 */
export function applyCollisionToLayer(
  mapbox: mapboxgl.Map,
  layer: string,
  properties: Record<string, unknown>,
  density: number
) {
  if (!mapbox.getLayer(layer)) return;
  const isLinePlacement = mapbox.getLayoutProperty(layer, "symbol-placement") === "line";
  for (const [key, value] of Object.entries(properties)) {
    if (isLinePlacement && key === "text-variable-anchor") continue;
    mapbox.setLayoutProperty(layer, key, value);
  }
  // For line-placement layers, adjust symbol-spacing to control label repetition along lines
  if (isLinePlacement) {
    const spacing =
      density >= DENSITY_FULL
        ? MIN_SYMBOL_SPACING
        : density >= DENSITY_HIGH
        ? Math.round(
            MIN_SYMBOL_SPACING +
              (DEFAULT_SYMBOL_SPACING - MIN_SYMBOL_SPACING) * (1 - density)
          )
        : DEFAULT_SYMBOL_SPACING;
    mapbox.setLayoutProperty(layer, "symbol-spacing", spacing);
  }
}

/**
 * Applies collision behavior to all label layers based on per-layer density.
 */
export function updateLabelCollisionBehavior(
  mapbox: mapboxgl.Map,
  densityMap: Record<string, number>
) {
  const labelLayers = getLabelLayerIds(mapbox);

  for (const layer of labelLayers) {
    const densityKey = getDensityKeyForLayer(layer);
    const density = densityMap[densityKey] ?? DEFAULT_DENSITY;
    const properties = getCollisionProperties(density);
    applyCollisionToLayer(mapbox, layer, properties, density);
  }
}

interface UseLabelDensityParams {
  mapboxRef: MutableRefObject<mapboxgl.Map>;
  txnIdRef: MutableRefObject<{ id: string; facilityId: string }>;
  isMapInitialized: boolean;
}

export function useLabelDensity({
  mapboxRef,
  txnIdRef,
  isMapInitialized
}: UseLabelDensityParams) {
  const labelDensityMap = useMapStore(selectLabelDensity);
  const zoom = useMapStore(selectMapZoom) ?? 0;
  const isBelowMinZoom = zoom <= LABEL_DENSITY_MIN_ZOOM;
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const updateLabelsRef = useRef(updateLabelsImpl);

  const getEffectiveDensity = useCallback(
    (layer: string) => {
      const key = getDensityKeyForLayer(layer);
      // Only restrict well/facility density at low zoom; shapefile labels use vector tiles
      if (
        isBelowMinZoom &&
        key !== SHAPEFILE_NAME_LABELS_LAYER &&
        key !== SHAPEFILE_DETAILS_LABEL_LAYER
      ) {
        return DEFAULT_DENSITY;
      }
      return getFromRecord(DEFAULT_DENSITY)(labelDensityMap, key);
    },
    [isBelowMinZoom, labelDensityMap]
  );

  function updateLabelsImpl(id: string, layer: string, density?: number) {
    const mapbox = mapboxRef.current;
    if (!mapbox || !id) {
      return;
    }

    // Cancel any in-flight request for this specific layer to prevent stale responses
    abortControllersRef.current[layer]?.abort();
    const controller = new AbortController();
    abortControllersRef.current[layer] = controller;
    const signal = controller.signal;

    const effectiveDensity = density ?? getEffectiveDensity(layer);

    async function fetchAndSetLabels() {
      try {
        const params: Record<string, unknown> = {};
        if (effectiveDensity > 0) {
          params.density = effectiveDensity;
        }

        const response = await axios.get(
          `${mapServiceEndpoint}/well/label/${Math.floor(mapbox.getZoom())}/${id}.json`,
          { params, signal }
        );

        if (signal.aborted || !mapboxRef.current || !response.data?.features?.length) {
          return;
        }

        const source = mapboxRef.current.getSource(layer) as mapboxgl.GeoJSONSource;
        if (source) {
          source.setData(response.data);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          Sentry.captureException(error);
        }
      }
    }

    fetchAndSetLabels();
  }

  // Keep ref in sync so debounced closure always calls the latest version
  updateLabelsRef.current = updateLabelsImpl;

  // Stable wrapper for external callers (MapboxWrapper)
  const updateLabels = useCallback(
    (id: string, layer: string, density?: number) =>
      updateLabelsRef.current(id, layer, density),
    []
  );

  const effectiveDensityMap = useMemo(() => {
    if (typeof labelDensityMap !== "object" || labelDensityMap === null) return {};
    if (!isBelowMinZoom) return labelDensityMap;
    // At low zoom, only restrict well/facility density; preserve shapefile density
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(labelDensityMap)) {
      if (key === SHAPEFILE_NAME_LABELS_LAYER || key === SHAPEFILE_DETAILS_LABEL_LAYER) {
        result[key] = value;
      } else {
        result[key] = DEFAULT_DENSITY;
      }
    }
    return result;
  }, [isBelowMinZoom, labelDensityMap]);

  const debouncedDensityUpdate = useMemo(
    () =>
      _debounce((densityMap: Record<string, number>) => {
        if (!isMapInitialized) return;
        const mapbox = mapboxRef.current;
        if (!mapbox) return;

        // Re-fetch well/facility labels with explicit density from the map
        if (txnIdRef.current) {
          const wellId = txnIdRef.current.id;
          const facilityId = txnIdRef.current.facilityId;

          if (wellId && wellId !== CLEAR_MAP_ID) {
            const wellDensity =
              densityMap[getDensityKeyForLayer(WELL_LABEL_LAYER)] ?? DEFAULT_DENSITY;
            updateLabelsRef.current(wellId, WELL_LABEL_LAYER, wellDensity);
          }
          if (facilityId && facilityId !== CLEAR_MAP_ID) {
            const facDensity =
              densityMap[getDensityKeyForLayer(FACILITY_LABEL_LAYER)] ?? DEFAULT_DENSITY;
            updateLabelsRef.current(facilityId, FACILITY_LABEL_LAYER, facDensity);
          }
        }

        updateLabelCollisionBehavior(mapbox, densityMap);
        mapbox.triggerRepaint();
      }, DENSITY_DEBOUNCE_MS),
    [isMapInitialized, mapboxRef, txnIdRef]
  );

  useEffect(() => {
    debouncedDensityUpdate(effectiveDensityMap);
    return () => {
      debouncedDensityUpdate.cancel();
      for (const controller of Object.values(abortControllersRef.current)) {
        controller.abort();
      }
      abortControllersRef.current = {};
    };
  }, [effectiveDensityMap, debouncedDensityUpdate]);

  return { updateLabels };
}
